/**
 * Which catalog reports the Queue page's "Queue a report" picker can actually
 * queue — and what to say about the ones it cannot.
 *
 * ## Why a report can be un-queueable from that picker
 *
 * The picker offers the whole catalog but builds its job as `kind: 'generic'`,
 * i.e. CB's generic report engine driven by `cb_Reports` column metadata. The
 * 21 SPECIALIZED reports (an `actionKey` on the catalog row) don't work that
 * way: their filters live on their own filter class, so
 * `GET /reports/:id/definition` answers **200 with `columns: []`**.
 *
 * That produced two bugs with one cause:
 *
 *  1. The modal rendered the empty column list as "This report has no filters."
 *     — a confident false claim about a financial report's scope. Aged A/R (21)
 *     is the reported case.
 *  2. Queueing one anyway was accepted: `POST /reports/jobs` defaults an
 *     unrecognised kind to `generic` (jobs/routes.js) with no guard. Verified
 *     live on QA 2026-08-11 — report 21 through the generic engine reports
 *     `percentComplete: 100, done: true, errored: false` and THEN 500s on
 *     `GET /reports/:id/data`. It fails only after claiming success.
 *
 * These reports queue correctly from their own pages, via SpecializedFilterBar
 * (`kind: 'specialized'`), so the answer is to route the user there rather than
 * hide the report or let them book a run that cannot finish.
 *
 * `actionKey` is already on the catalog list payload (cb-api reports/routes.js
 * `/list`), so this needs no backend change.
 */

/**
 * @param {{id: number|string, name: string, actionKey?: string}|null} option
 *   a row from the picker's report options
 * @returns {{message: string, to: string, linkLabel: string}|null}
 *   null when the report IS queueable from the picker
 */
export const unsupportedQueuePick = (option) => {
  if (!option?.actionKey) return null
  return {
    message: `${option.name} is a specialized report — its filters live on its own page, not in the report catalog, so they can't be set or queued from here.`,
    to: `/reports/${option.id}`,
    linkLabel: `Open ${option.name} to queue or schedule it`,
  }
}

export default unsupportedQueuePick
